import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input, OnChanges,
  Output, SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CalculateColumnModel } from '../../../data-source/models/calculate-column.model';
import { CalculateColumnModeType } from '../../../dashboard/models/calculateColumnModeType';

import { DAXAutocomplete, SuggestionModel } from 'dax-autocomplete';
import { DaxDatasorcesModel } from '../../models/dax-datasorces.model';


@Component({
  selector: 'morva-dax-formula-bar',
  templateUrl: './dax-formula-bar.component.html',
  styleUrls: ['./dax-formula-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DaxFormulaBarComponent implements OnChanges {

  @ViewChild('nameInput') nameInput: ElementRef;
  @ViewChild('textBox') textBox: ElementRef;
  @ViewChild('suggestionList') suggestionList: ElementRef;

  @Input() calculateColumn: Partial<CalculateColumnModel>;
  @Input() calculateColumnMode: CalculateColumnModeType;
  @Input() dataSourcesForDaxBar: DaxDatasorcesModel[];
  @Input() loading: boolean;
  @Input() editMode: boolean;
  @Input() serverError: string;
  @Output() deactivateCalculateColumnModeEvent = new EventEmitter();
  @Output() createCalculateColumnModeEvent = new EventEmitter<Partial<CalculateColumnModel>>();
  @Output() updateCalculateColumnModeEvent = new EventEmitter<Partial<CalculateColumnModel>>();
  @Output() openDaxHelpEvent = new EventEmitter();

  clientErrors: string[] = [];
  textBoxHeight = 22;
  textBoxCurserPosition = 0;
  isTextBoxFocused = true;
  daxService = new DAXAutocomplete();
  daxFormula = '';
  suggestions: SuggestionModel[] = [];
  suggestionIndex = 0;

  constructor(private changeDetectorRef: ChangeDetectorRef) { }

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      if (this.suggestions.length > 0) {
        event.preventDefault();
        if (!this.isTextBoxFocused && this.suggestionIndex < this.suggestions.length - 1) {
          this.suggestionIndex++;
        }
        this.changeDetectorRef.detectChanges();
        this.suggestionList.nativeElement.children[this.suggestionIndex].firstChild.focus();
        this.isTextBoxFocused = false;
      }
    } else if (event.key === 'ArrowUp') {
      if (this.suggestionIndex > 0) {
        event.preventDefault();
        if (!this.isTextBoxFocused) {
          this.suggestionIndex--;
        }
        this.changeDetectorRef.detectChanges();
        this.suggestionList.nativeElement.children[this.suggestionIndex].firstChild.focus();
        this.isTextBoxFocused = false;
      }
    } else if (event.key === 'Escape') {
      if (!this.isTextBoxFocused && this.suggestions.length > 0) {
        this.isTextBoxFocused = true;
        this.textBox.nativeElement.focus();
      } else if (this.isTextBoxFocused && this.suggestions.length > 0) {
        this.suggestions = [];
      }
      this.suggestionIndex = 0;
    } else if (/[\u0600-\u06FFa-zA-Z0-9_ ]/.test(event.key) && event.key !== 'Enter') {
      if (!this.isTextBoxFocused && this.suggestions.length > 0) {
        this.isTextBoxFocused = true;
        this.textBox.nativeElement.focus();
      }
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['calculateColumn'] && this.calculateColumn) {
      setTimeout(() => {
        this.nameInput.nativeElement.innerText = this.calculateColumn.name || this.calculateColumn.columnName ?
          this.calculateColumn.name || this.calculateColumn.columnName :
          (this.calculateColumnMode === 'Measure' ? 'new measure' : 'new column');
        this.daxFormula =
          this.calculateColumn.expression ? this.calculateColumn.expression : '';
        setTimeout(() => {
          this.textBox.nativeElement.focus();
          this.textBoxHeight = this.textBox.nativeElement.scrollHeight;
          this.changeDetectorRef.detectChanges();
        }, 0);
      }, 0);
    }
    if (changes['dataSourcesForDaxBar']) {
      if (this.dataSourcesForDaxBar) {
        this.setTablesAndColumns(this.dataSourcesForDaxBar);
      }
    }
  }

  setTablesAndColumns(datasources: DaxDatasorcesModel[]): void {
    const tables = [];
    const columns = [];
    datasources.forEach(datasource => {
      tables.push({
        name: datasource.name,
      });
      datasource.columns.forEach(column => {
        columns.push({
          name: column.name,
          columnParentName: datasource.name,
          columnDataType: column.dataType,
          columnType: column.columnType,
          columnDataModuleType: column.dataModuleType,
        });
      });
    });
    this.daxService.setTables(tables);
    this.daxService.setColumns(columns);
  }

  onChangeFormula(event: any) {
    this.isTextBoxFocused = true;
    this.textBoxHeight = this.daxFormula.split('\n').length * 18 + 4;
    this.textBoxCurserPosition = event.target.selectionStart;
    this.suggestionIndex = 0;
    this.suggestions = this.daxService.autocomplete(this.daxFormula, this.textBoxCurserPosition);
    this.changeDetectorRef.detectChanges();
  }

  onFocusOut() {
    this.isTextBoxFocused = false;
  }

  onSuggestionClick(suggestion: SuggestionModel) {
    const newDax = this.daxService.insertSuggestion(
      this.daxFormula,
      suggestion,
      this.textBoxCurserPosition);

    const lengthDiff = newDax.length - this.daxFormula.length;
    this.textBoxCurserPosition += lengthDiff;
    if (suggestion.optionType === 'keyword') {
      this.textBoxCurserPosition -= 1;
    }

    this.daxFormula = newDax;
    setTimeout(() => {
      this.textBox.nativeElement.setSelectionRange(this.textBoxCurserPosition, this.textBoxCurserPosition);
      this.textBox.nativeElement.focus();
    }, 0);

    this.suggestions = [];
  }

  getSuggestionIcon(suggestion: SuggestionModel): string {
    if (suggestion.optionType === 'table') {
      return 'dataSource';
    } else {
      const icon = this.getIconBaseOnColumnType(suggestion.columnType) ??
        this.getIconBaseOnDataType(suggestion?.columnDataType);
      return this.getIconBaseOnColumnType(suggestion.columnType) ??
        this.getIconBaseOnDataType(suggestion?.columnDataType);
    }
  }


  getIconBaseOnColumnType(dataType: string | undefined) {
    switch (dataType) {
      case 'CalculateColumn':
        return 'customColumn';
      case 'ConditionalColumn':
        return 'customColumn';
      case 'Measure':
        return 'measure';
      default:
        return undefined;
    }
  }
  getIconBaseOnDataType(dataType: string | undefined) {
    switch (dataType) {
      case 'Binary':
        return 'quality';
      case 'Boolean':
        return 'quality';
      case 'Currency':
        return 'quantity';
      case 'Date':
        return 'calendar';
      case 'DecimalNumber':
        return 'quantity';
      case 'Text':
        return 'quality';
      case 'WholeNumber':
        return 'quantity';
    }
  }

  onSubmit() {
    if (!this.calculateColumnHasError()) {
      this.suggestions = [];
      if (this.editMode) {
        this.updateCalculateColumn();
      } else {
        this.createCalculateColumn();
      }
    }
  }

  calculateColumnHasError(): boolean {
    this.clientErrors = [];
    if (this.nameInput.nativeElement.innerText <= 0) {
      this.clientErrors.push('نامی برای ستون جدید نوشته نشده‌است.');
    } else if (this.daxFormula.length <= 0) {
      this.clientErrors.push('فرمولی برای ستون جدید نوشته نشده‌است.');
    } else {
      return false;
    }
    this.changeDetectorRef.detectChanges();
    return true;
  }

  createCalculateColumn(): void {
    this.createCalculateColumnModeEvent.emit({
      columnName: this.nameInput.nativeElement.innerText,
      expression: this.daxFormula,
      dataSourceId: this.calculateColumn.dataSourceId,
    });
  }

  updateCalculateColumn(): void {
    const updateCalculateColumn = { ...this.calculateColumn };
    updateCalculateColumn.columnName = this.nameInput.nativeElement.innerText;
    updateCalculateColumn.expression = this.daxFormula;
    this.updateCalculateColumnModeEvent.emit(updateCalculateColumn);
  }

  openDaxHelpDialog() {
    this.openDaxHelpEvent.emit();
  }

  deactivateCalculateColumnMode(): void {
    this.deactivateCalculateColumnModeEvent.emit();
  }

}
